import { Delete, Edit } from "@mui/icons-material";
import { Divider, IconButton, Link, Stack, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { SetStateAction, useState } from "react";
import { fMonthDayYearDotNotation } from "../../../utils/formatTime";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { ContactInteraction } from "./types";
import StickyTable from "../../../shared/StickyTable";

const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

interface InteractionsTableProps {
  interactions: ContactInteraction[];
  onDeleteInteraction: (id: number) => Promise<void>;
  onEditInteraction: (value: SetStateAction<ContactInteraction | undefined>) => void;
}

export default function InteractionsTable({
  interactions,
  onDeleteInteraction,
  onEditInteraction
}: InteractionsTableProps) {
  const [interactionToDelete, setInteractionToDelete] = useState<ContactInteraction | null>(null);

  const handleDeleteClick = (interaction: ContactInteraction) => {
    setInteractionToDelete(interaction);
  };

  const handleDeleteConfirm = async () => {
    if (interactionToDelete) {
      await onDeleteInteraction(interactionToDelete.id);
      setInteractionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setInteractionToDelete(null);
  };

  return (
    <>
      <Divider />
      <StickyTable size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cultivator</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Follow&nbsp;Ups</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Order</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Attendee</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {interactions.map((interaction) => (
            <TableRow key={interaction.id} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
              <TableCell>{interaction.clientCultivator?.fullName}</TableCell>
              <TableCell>
                {interaction.interactionDate ? fMonthDayYearDotNotation(interaction.interactionDate) : ""}
              </TableCell>
              <TableCell>
                <Stack spacing={0.5}>
                  {(interaction.followUps || []).filter((followUp) => followUp.followUpDate).map((followUp) => (
                    <span key={followUp.id || followUp.followUpDate}>
                      {fMonthDayYearDotNotation(followUp.followUpDate!)}
                    </span>
                  ))}
                </Stack>
              </TableCell>
              <TableCell>{interaction.type?.name}</TableCell>
              <TableCell>
                {interaction.orderNumber && (
                  <Link href={`${momentumUrl}new-home/${interaction.orderNumber}`} target="_blank" underline="hover">
                    {interaction.orderNumber}
                  </Link>
                )}
              </TableCell>
              <TableCell>{interaction.interactionCategory}</TableCell>
              <TableCell>{interaction.reason?.name}</TableCell>
              <TableCell>{interaction.source?.name}</TableCell>
              <TableCell>{interaction.event?.name}</TableCell>
              <TableCell>{interaction.notes}</TableCell>
              <TableCell>{interaction.additionalAttendee?.fullName}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <IconButton size="small" onClick={() => onEditInteraction(interaction)} aria-label="edit interaction">
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteClick(interaction)} aria-label="delete interaction">
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StickyTable>
      {interactionToDelete && (
        <DeleteConfirmationModal
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          interaction={interactionToDelete}
        />
      )}
    </>
  );
}
